/**
 * Admin UI for managing per-module reference PDFs.
 *
 * Shows Contract Details + each module from template schema with upload/delete controls.
 * Used when template referencePdfMode is 'per-module'.
 */

import { AlertCircle } from 'lucide-react';
import type { OrgTemplate } from '../../types/orgTemplate';
import ModulePdfUpload from './ModulePdfUpload';

interface PerModulePdfManagerProps {
  template: OrgTemplate;
}

export default function PerModulePdfManager({ template }: PerModulePdfManagerProps) {
  const modules = template.schema?.modules || [];

  return (
    <div className="space-y-3">
      {/* Contract Details PDF */}
      <ModulePdfUpload
        templateId={template.id}
        moduleId="contract-details"
        label="Contract Details"
        currentKey={template.referencePdfKeys?.contractDetails}
        isContractDetails={true}
      />

      {/* Module PDFs */}
      {modules.map((module) => (
        <ModulePdfUpload
          key={module.id}
          templateId={template.id}
          moduleId={module.id}
          label={module.name}
          currentKey={template.referencePdfKeys?.modules?.[module.id]}
        />
      ))}

      {/* No modules warning */}
      {modules.length === 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-body-sm font-medium text-amber-800 dark:text-amber-300">No modules defined</p>
            <p className="text-caption text-amber-700 dark:text-amber-400 mt-1">
              Configure modules in the template schema to upload per-module reference PDFs.
              Only the Contract Details PDF can be uploaded until modules are defined.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
